import { baseApi } from '@/utils/api.js';

const ingredientsApi = baseApi.enhanceEndpoints({
  addTagTypes: ['Ingredients'],
}).injectEndpoints({
  endpoints: (builder) => ({
    searchIngredients: builder.query({
      query: ({ name, limit }) => ({
        url: `/ingredients/.search`,
        method: 'POST',
        body: limit ? { name, limit } : { name },
      }),
      providesTags: ['Ingredients'],
    }),
    getIngredients: builder.query({
      query: () => ({
        url: '/ingredients/',
        method: 'GET',
      }),
      providesTags: ['Ingredients'],
    }),
    getIngredient: builder.query({
      query: ({ id }) => ({
        url: `/ingredients/${id}`,
        method: 'GET',
      }),
      providesTags: ['Ingredients'],
    }),
    createIngredient: builder.mutation({
      query: ({ data }) => ({
        url: '/ingredients/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ingredients'],
    }),
    updateIngredient: builder.mutation({
      query: ({ id, data }) => ({
        url: `/ingredients/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Ingredients'],
    }),
    // A single-cell edit only touches one ingredient, so patch it directly into
    // the cached list instead of invalidating -- invalidating would refetch the
    // whole table for a one-field change, which is slow and needless traffic on
    // a bad connection, and (until that refetch lands) leaves the row showing
    // its pre-edit value.
    patchIngredient: builder.mutation({
      query: ({ id, data }) => ({
        url: `/ingredients/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const patchInto = (arg) => dispatch(
            ingredientsApi.util.updateQueryData('getIngredients', arg, (draft) => {
              const index = draft.data.findIndex((ingredient) => ingredient.id === response.data.id);
              if (index !== -1) draft.data[index] = response.data;
            })
          );
          // getIngredients is called both as useGetIngredientsQuery({}) and
          // useGetIngredientsQuery() (no arg) elsewhere -- different cache keys,
          // both need the patch.
          patchInto({});
          patchInto(undefined);
        } catch {
          // Request failed -- the caller sees result.error and shows it; there is
          // nothing in the cache to correct.
        }
      },
    }),
    deleteIngredient: builder.mutation({
      query: ({ id }) => ({
        url: `/ingredients/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Ingredients'],
    }),
    bulkUpdateIngredients: builder.mutation({
      query: ({ ids, changes }) => ({
        url: '/ingredients/.bulk-update',
        method: 'POST',
        body: { ids, changes },
      }),
      invalidatesTags: ['Ingredients'],
    }),
  }),
});

export const {
  useSearchIngredientsQuery,
  useGetIngredientsQuery,
  useGetIngredientQuery,
  useUpdateIngredientMutation,
  usePatchIngredientMutation,
  useDeleteIngredientMutation,
  useCreateIngredientMutation,
  useBulkUpdateIngredientsMutation,
} = ingredientsApi;
